import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import {getDeviceTypesData} from '../../consts/device/deviceTypesData';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import ICC_PRODUCT_STATUS_TYPES from '../../enums/icc/iccProductStatusTypes';
import {setConsumerProductsInfo} from '../../state/ducks/consumer';
import {updateIotDeviceData} from '../../state/ducks/iotDevice/actions';
import {selectMuleSoftConsumerProducts} from '../../state/selectors/consumer';
import {selectIotDeviceData} from '../../state/selectors/iotDevice';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import devicePlatformService from '../device/devicePlatformService';
import deviceTypesService from '../device/deviceTypesService';
import ConsumerAssetsError from '../errors/consumerAssetsError';
import iccConsumerProducts from '../icc/iccConsumerProducts';
import log from '../logger/log';
import {getMediaId} from '../mapping/muleSoftMarketMapping';
import marketConfigService from '../marketConfigService';
import pairingService from '../pairingService';
import productHelpers from '../product/productHelpers';
import productService from '../product/productService';
import appRouterService from '../route/appRouterService';
import muleSoftMarketService from './muleSoftMarketService';

const getMuleSoftDetailedProducts = async () => {
    try {
        await muleSoftMarketService.getConsumerAssets();
        setMuleSoftProductsInfo();
        await iccConsumerProducts.getProductsAssetsByProducts();
    } catch {
        log.info(`muleSoftProductService: getMuleSoftDetailedProducts failed`);
        appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
    }
};

const updateMuleSoftDeviceStatus = (isHolder, status) => {
    const currentIotDevice = selectIotDeviceData(getState());
    const data = {
        [isHolder ? 'holder' : 'device']: {
            ...currentIotDevice[isHolder ? 'holder' : 'device'],
            status,
        },
    };

    dispatch(updateIotDeviceData({...data}));
};

const linkUnlinkTemplate = async ({codentify, isHolder, isLink}) => {
    const country = appConfig.getMuleSoftCountryCode();

    updateMuleSoftDeviceStatus(isHolder, ICC_PRODUCT_STATUS_TYPES.REGISTERING);

    const targetRequest = isLink
        ? muleSoftMarketService.linkConsumerProduct
        : muleSoftMarketService.unlinkConsumerProduct;

    const response = await targetRequest(codentify, country);

    if (isLink) {
        await muleSoftMarketService.getConsumerAssets();
    } else {
        await getConsumerAssetsInterval();
    }

    const iotDevice = selectIotDeviceData(getState());
    const {device, holder} = iotDevice || {};
    const {codentify: chargerCodentify, status: chargerStatus} = device || {};
    const {status: holderStatus} = holder || {};
    const status = codentify === chargerCodentify ? chargerStatus : holderStatus;
    const result = isLink
        ? {isRegistered: productService.getIsRegistered(status), response}
        : {isUnregistered: productService.getIsUnregistered(status), response};

    return result;
};

const linkProduct = async (codentify, isHolder) => await linkUnlinkTemplate({codentify, isHolder, isLink: true});
const unlinkProduct = async (codentify, isHolder) => await linkUnlinkTemplate({codentify, isHolder});

const setMuleSoftProductsInfo = () => {
    const consumerProducts = selectMuleSoftConsumerProducts(getState());

    const mapConsumerProducts = consumerProducts.map((product) => {
        const type = devicePlatformService.getDeviceTypeBySerialNumber(product.deviceSerialNumber);
        const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({
            material: {materialBrandFamily: product.productFamily},
        });
        const preparedType =
            DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER === type && isOriginalDuoDevice
                ? DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER
                : type;

        const deviceTypeData = getDeviceTypesData()[preparedType];
        const transformedDeviceSerialNumber = productHelpers.getTransformedDeviceSerialNumber(
            product.deviceSerialNumber
        );

        return {
            deviceSerialNumber: transformedDeviceSerialNumber,
            mediaId: getMediaId(product),
            defaultProductName: deviceTypesService.getProductNameByType(preparedType),
            isDeviceSupported: marketConfigService.isDeviceSupported(preparedType),
            type: preparedType,
            codentify: product.codentify,
            isHolder: deviceTypeData?.isHolder,
            materialColor: product.deviceColor,
        };
    });

    dispatch(setConsumerProductsInfo(mapConsumerProducts));
};

const getConsumerAssetsInterval = async () => {
    const REQUEST_INTERVAL = 5 * 1000;
    const REQUEST_REPEAT_COUNT = 20;
    let currentRequest = 0;

    const intervalFunction = async () => {
        try {
            currentRequest++;
            await muleSoftMarketService.getConsumerAssets();
        } catch {
            if (currentRequest === REQUEST_REPEAT_COUNT) {
                pairingService.disconnectAndForwardToMyDevicesPage();

                throw new ConsumerAssetsError();
            }

            await helpers.timeout(REQUEST_INTERVAL);
            await intervalFunction();
        }
    };

    await intervalFunction();
};

export default {
    unlinkProduct,
    linkProduct,
    getMuleSoftDetailedProducts,
    getConsumerAssetsInterval,
    updateMuleSoftDeviceStatus,
};
